import { useEffect, useState } from "react";
import { Copy, QrCode, Wallet, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "./ui/button";

/**
 * UPI Tap-to-Pay — two routes side-by-side:
 *   (a) Seller VPA (buyer -> seller directly, zero fees)
 *   (b) Fairvest escrow VPA (if admin has configured it in .env)
 */
export default function UpiPayPanel({ type }) {
  const [route, setRoute] = useState("seller");
  const [payeeVpa, setPayeeVpa] = useState("");
  const [payeeName, setPayeeName] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState(
    type === "land" ? "Land token" : type === "silver" ? "Silver purchase" : "Gold purchase",
  );
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState(false);
  const [escrowAvailable, setEscrowAvailable] = useState(false);
  const [escrowName, setEscrowName] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/purchase/upi-config");
        setEscrowAvailable(!!data.escrow_available);
        setEscrowName(data.escrow_name || "Fairvest Escrow");
      } catch {
        setEscrowAvailable(false);
      }
    })();
  }, []);

  const generate = async () => {
    if (route === "seller") {
      if (!payeeVpa.trim() || !payeeName.trim()) {
        toast.error("Enter shop / seller UPI ID and name");
        return;
      }
    } else if (route === "escrow" && !escrowAvailable) {
      toast.error("Fairvest escrow VPA not yet configured");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/purchase/upi-link", {
        payee_vpa: route === "seller" ? payeeVpa.trim() : "escrow@placeholder",
        payee_name: route === "seller" ? payeeName.trim() : "Fairvest",
        amount: amount ? Number(amount) : null,
        note,
        route,
      });
      setLink(data);
      toast.success("UPI link ready — tap to open your UPI app");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not build UPI link";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!link?.upi_link) return;
    try {
      await navigator.clipboard.writeText(link.upi_link);
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed — long-press the button on mobile");
    }
  };

  return (
    <div
      className="bg-white border border-line rounded-sm p-5"
      data-testid={`upi-pay-${type}`}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <Wallet className="w-4 h-4 text-moss" />
        <span className="font-serif-display text-lg text-stone_brand-dark font-medium">
          Tap-to-pay via UPI
        </span>
      </div>
      <p className="text-xs text-stone_brand mb-3">
        Opens GPay / PhonePe / Paytm / BHIM directly. No gateway, zero fees — money moves buyer → seller (or via Fairvest escrow, if enabled).
      </p>

      {/* Route toggle */}
      <div className="flex gap-2 mb-4" data-testid="upi-route-toggle">
        <button
          onClick={() => setRoute("seller")}
          className={
            "flex-1 px-3 py-2 text-xs font-mono uppercase rounded-sm border transition " +
            (route === "seller"
              ? "bg-stone_brand-dark text-bone-50 border-stone_brand-dark"
              : "bg-white text-stone_brand border-line hover:border-stone_brand")
          }
          data-testid="upi-route-seller"
        >
          Direct to seller
          <div className="text-[0.6rem] normal-case font-sans mt-0.5 opacity-70">
            Zero fees
          </div>
        </button>
        <button
          onClick={() => setRoute("escrow")}
          disabled={!escrowAvailable}
          className={
            "flex-1 px-3 py-2 text-xs font-mono uppercase rounded-sm border transition " +
            (route === "escrow"
              ? "bg-moss text-bone-50 border-moss"
              : escrowAvailable
                ? "bg-white text-stone_brand border-line hover:border-moss"
                : "bg-bone-50 text-stone_brand-light border-line cursor-not-allowed")
          }
          data-testid="upi-route-escrow"
          title={escrowAvailable ? "" : "Escrow VPA not yet configured"}
        >
          <ShieldCheck className="w-3 h-3 inline mr-1" />
          Escrow ({escrowName})
          <div className="text-[0.6rem] normal-case font-sans mt-0.5 opacity-70">
            {escrowAvailable ? "Protected" : "Coming soon"}
          </div>
        </button>
      </div>

      {/* Seller inputs only if route=seller */}
      {route === "seller" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
          <input
            value={payeeVpa}
            onChange={(e) => setPayeeVpa(e.target.value)}
            placeholder="Seller UPI ID (e.g. shop@icici)"
            className="h-10 px-3 text-sm rounded-sm border border-line bg-white focus:outline-none focus:ring-1 focus:ring-moss"
            data-testid="upi-seller-vpa"
          />
          <input
            value={payeeName}
            onChange={(e) => setPayeeName(e.target.value)}
            placeholder="Seller / shop name"
            className="h-10 px-3 text-sm rounded-sm border border-line bg-white focus:outline-none focus:ring-1 focus:ring-moss"
            data-testid="upi-seller-name"
          />
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
        <input
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="Amount ₹ (optional)"
          className="h-10 px-3 text-sm rounded-sm border border-line bg-white focus:outline-none focus:ring-1 focus:ring-moss"
          data-testid="upi-amount"
        />
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Note (e.g. Gold 10g 22K)"
          className="h-10 px-3 text-sm rounded-sm border border-line bg-white focus:outline-none focus:ring-1 focus:ring-moss"
          data-testid="upi-note"
        />
      </div>

      <Button
        onClick={generate}
        disabled={busy}
        className="rounded-sm bg-stone_brand-dark hover:bg-stone_brand text-bone-50 h-10 gap-1.5 w-full sm:w-auto"
        data-testid="upi-generate"
      >
        <QrCode className="w-4 h-4" />
        {busy ? "Building…" : "Generate UPI link"}
      </Button>

      {link?.upi_link && (
        <div
          className="mt-4 p-3 bg-moss-bg/30 border border-moss/30 rounded-sm space-y-2"
          data-testid="upi-link-result"
        >
          <div className="text-[0.65rem] font-mono uppercase tracking-widest text-moss">
            Ready — tap on mobile
          </div>
          <div className="flex flex-wrap gap-2">
            <a
              href={link.upi_link}
              className="inline-flex items-center gap-1.5 px-4 h-10 rounded-sm bg-moss hover:bg-moss-hover text-bone-50 text-sm font-medium"
              data-testid="upi-open-link"
            >
              <Wallet className="w-4 h-4" />
              Open UPI app to pay
            </a>
            <button
              onClick={copy}
              className="inline-flex items-center gap-1.5 px-3 h-10 rounded-sm border border-line bg-white text-sm text-stone_brand-dark hover:border-stone_brand"
              data-testid="upi-copy-link"
            >
              <Copy className="w-3.5 h-3.5" />
              Copy link
            </button>
          </div>
          <div className="text-[0.7rem] font-mono text-stone_brand-light break-all">
            {link.upi_link}
          </div>
        </div>
      )}
    </div>
  );
}
